/**
 * TanStack Query hooks for `click-log-report`.
 *
 * - `useClickLogList` — infinite cursor-paginated list keyed on filters.
 * - `useClickLogCount` — cheap total preview for the export warning banner.
 * - `useExportClicksCsv` — mutation returning `{blob, rowCount, filename}`;
 *    the page composes the browser download via an in-memory object URL.
 */
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	type UseInfiniteQueryResult,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	clickLogReportQueryKeys,
	exportClicksCsv,
	previewClickCount,
	queryClicks,
	type ClickLogFilters,
	type ClickLogListResponse,
} from "./api";

export function useClickLogList(
	filters: ClickLogFilters,
): UseInfiniteQueryResult<
	{ pages: ClickLogListResponse[]; pageParams: Array<string | null> },
	Error
> {
	return useInfiniteQuery({
		queryKey: clickLogReportQueryKeys.list(filters),
		queryFn: ({ pageParam }) =>
			queryClicks({ filters, cursor: pageParam as string | null }),
		initialPageParam: null as string | null,
		getNextPageParam: (last) => last.meta.next_cursor ?? null,
	});
}

export function useClickLogCount(
	filters: ClickLogFilters,
	enabled = true,
): UseQueryResult<number, Error> {
	return useQuery({
		queryKey: clickLogReportQueryKeys.count(filters),
		queryFn: () => previewClickCount({ filters }),
		enabled,
		staleTime: 30_000,
	});
}

export function useExportClicksCsv(): UseMutationResult<
	{ blob: Blob; rowCount: number | null; filename: string },
	Error,
	{ filters: ClickLogFilters }
> {
	return useMutation({
		mutationFn: (args) => exportClicksCsv(args),
	});
}
